import * as Logger from "js-logger";
import AlertComponent from "slotworks/components/alert/alert-component";
import BannerComponent from "slotworks/components/banner/banner-component";
import BetBarComponent from "slotworks/components/betbar/bet-bar-component";
import { CountMode } from "slotworks/components/bigwin/big-win-component";
import BigWinComponent from "slotworks/components/bigwin/big-win-component";
import CelebrateWinComponent from "slotworks/components/celebrate-win/celebrate-win-component";
import FooterComponent from "slotworks/components/footer/footer-component";
import HeaderComponent from "slotworks/components/header/header-component";
import LinesComponent from "slotworks/components/lines/lines-component";
import MatrixComponent from "slotworks/components/matrix/matrix-component";
import AbstractReelTransition from "slotworks/components/matrix/reel/transition-behaviours/abstract-reel-transition";
import AnimateReelTransition from "slotworks/components/matrix/reel/transition-behaviours/animate/animate-reel-transition";
import ReelSpinner from "slotworks/components/matrix/reel/transition-behaviours/spin/reel-spinner";
import SpinReelTransition from "slotworks/components/matrix/reel/transition-behaviours/spin/spin-reel-transition";
import AbstractSymbolBehaviour from "slotworks/components/matrix/symbol/behaviours/abstract-symbol-behaviour";
import AnimateOverSymbolBehaviour from "slotworks/components/matrix/symbol/behaviours/animate-over-symbol-behaviour";
import AnimateSymbolBehaviour from "slotworks/components/matrix/symbol/behaviours/animate-symbol-behaviour";
import AnticipateAnimateOverSymbolBehaviour from "slotworks/components/matrix/symbol/behaviours/anticipate-animate-over-symbol-behaviour";
import AnticipateAnimateSymbolBehaviour from "slotworks/components/matrix/symbol/behaviours/anticipate-animate-symbol-behaviour";
import BlinkSymbolBehaviour from "slotworks/components/matrix/symbol/behaviours/blink-symbol-behaviour";
import DebugSymbolBehaviour from "slotworks/components/matrix/symbol/behaviours/debug-symbol-behaviour";
import ExpandAnimateSymbolBehaviour from "slotworks/components/matrix/symbol/behaviours/expand-animate-symbol-behaviour";
import ImageDimSymbolBehaviour from "slotworks/components/matrix/symbol/behaviours/image-dim-symbol-behaviour";
import LandAnimateOverSymbolBehaviour from "slotworks/components/matrix/symbol/behaviours/land-animate-over-symbol-behaviour";
import LandAnimateSymbolBehaviour from "slotworks/components/matrix/symbol/behaviours/land-animate-symbol-behaviour";
import LandPulseSymbolBehaviour from "slotworks/components/matrix/symbol/behaviours/land-pulse-symbol-behaviour";
import StickSymbolBehaviour from "slotworks/components/matrix/symbol/behaviours/stick-symbol-behaviour";
import StopBounceSymbolBehaviour from "slotworks/components/matrix/symbol/behaviours/stop-bounce-symbol-behaviour";
import TintDimSymbolBehaviour from "slotworks/components/matrix/symbol/behaviours/tint-dim-symbol-behaviour";
import ZoomSymbolBehaviour from "slotworks/components/matrix/symbol/behaviours/zoom-symbol-behaviour";
import PreloaderComponent from "slotworks/components/preloader/preloader-component";
import PromptComponent from "slotworks/components/prompt/prompt-component";
import { ComponentConfigSchema } from "slotworks/config/component-config-schema";
import { BigWinConfig } from "slotworks/config/component-config-schema";
import SlotDefinition from "slotworks/model/slot-definition";
import GambleComponent from "./gamble/gamble-component";
import { DropReelTransition } from "./matrix/reel/transition-behaviours/drop/drop-reel-transition";
import ReelDropper from "./matrix/reel/transition-behaviours/drop/reel-dropper";
import AnimateBehindSymbolBehaviour from "./matrix/symbol/behaviours/animate-behind-symbol-behaviour";
import RemoveAnimateSymbolBehaviour from "./matrix/symbol/behaviours/remove-animate-symbol-behaviour";
import WobbleSymbolBehaviour from "./matrix/symbol/behaviours/wobble-symbol-behaviour";

export default class Components {

    public static config: ComponentConfigSchema;

    public static PreloaderComponentType = PreloaderComponent;
    public static AlertComponentType = AlertComponent;
    public static MatrixComponentType = MatrixComponent;
    public static BigWinComponentType = BigWinComponent;
    public static LinesComponentType = LinesComponent;
    public static CelebrateWinComponentType = CelebrateWinComponent;
    public static BetBarComponentType = BetBarComponent;
    public static GambleComponentType = GambleComponent;
    public static PromptComponentType = PromptComponent;
    public static HeaderComponentType = HeaderComponent;
    public static FooterComponentType = FooterComponent;
    public static BannerComponentType = BannerComponent;
    public static SpinReelTransitionType = SpinReelTransition;
    public static DropReelTransitionType = DropReelTransition;
    public static AnimateReelTransitionType = AnimateReelTransition;

    public static preloader: PreloaderComponent;
    public static alert: AlertComponent;
    public static matrix: MatrixComponent;
    public static bigWin: BigWinComponent;
    public static lines: LinesComponent;
    public static celebrateWin: CelebrateWinComponent;
    public static betBar: BetBarComponent;
    public static gamble: GambleComponent;
    public static prompt: PromptComponent;
    public static header: HeaderComponent;
    public static footer: FooterComponent;
    public static banner: BannerComponent;

    /**
     * Sets up components after just 1 stage has loaded (preloader)
     */
    public static preinit() {
        this.createPreloader();
        this.createPrompts();
        this.createAlert();
        this.configReelSpinner();
    }

    /**
     * Initialise basic standard components, using the component config to set them up.
     * Might split this class up later to have a factory for each component
     */
    public static init() {
        this.destroyPreInit();

        this.createMatrix();
        this.createBigWin();
        this.createLines();
        this.createCelebrateWin();
        this.createPrompts();
        this.createBetBar();
        this.createHeader();
        this.createFooter();
        this.createBanner();
    }

    public static addDefaultSymbolBehaviours(matrix: MatrixComponent) {
        const config = Components.config.matrix;

        if (!config) {
            return;
        }

        // Add symbol behaviours (if configured)
        if (config.symbolBehaviours) {
            for (const symbolBehaviour of config.symbolBehaviours) {
                const behaviour = this.getSymbolBehaviourType(symbolBehaviour.id);

                if (behaviour) {
                    matrix.addDefaultSymbolBehaviour(behaviour, symbolBehaviour.symbols);
                }
            }
        }
    }

    public static add<T>(component: T) {
        Components.components.push(component);
    }

    public static get<T>(type: { new(...args: any[]): T }): T {
        return Components.components.find((value) => value instanceof type);
    }

    private static components: any[] = [];

    /**
     * Courtesy method to pull reel spinner stages from config
     */
    private static configReelSpinner() {
        const config = Components.config.matrix;

        const legacyStagesFormat = (stages: any) => {
            return (stages[0] instanceof Array) ? stages : [stages];
        };

        if (config.transition.options && config.transition.type) {
            if (config.transition.type === "spin") {
                if (config.transition.options.spinStages) {
                    ReelSpinner.spinStages = legacyStagesFormat(config.transition.options.spinStages);
                }
                if (config.transition.options.quickSpinStages) {
                    ReelSpinner.quickSpinStages = legacyStagesFormat(config.transition.options.quickSpinStages);
                } else {
                    ReelSpinner.quickSpinStages = ReelSpinner.spinStages;
                }
                if (config.transition.options.skipStages) {
                    ReelSpinner.skipStages = legacyStagesFormat(config.transition.options.skipStages);
                }
                if (config.transition.options.anticipationStages) {
                    ReelSpinner.anticipationStages = legacyStagesFormat(config.transition.options.anticipationStages);
                }

                if (config.transition.options.stopGap) {
                    ReelSpinner.stopGap = config.transition.options.stopGap;
                }
            } else if (config.transition.type === "cascade") {
                if (config.transition.options.dropDistance) {
                    ReelDropper.dropConfig.dropDistance = config.transition.options.dropDistance;
                }
                if (config.transition.options.dropDuration) {
                    ReelDropper.dropConfig.dropDuration = config.transition.options.dropDuration;
                }
                if (config.transition.options.symbolStaggerMin) {
                    ReelDropper.dropConfig.symbolStaggerMin = config.transition.options.symbolStaggerMin;
                }
                if (config.transition.options.symbolStaggerMax) {
                    ReelDropper.dropConfig.symbolStaggerMax = config.transition.options.symbolStaggerMax;
                }
            }
        }
    }

    private static destroyPreInit() {
        Components.preloader.destroy();
    }

    private static createPreloader() {
        this.preloader = new Components.PreloaderComponentType();
    }

    private static createMatrix() {
        const config = Components.config.matrix;

        if (!config) {
            return;
        }

        // Create matrix
        this.matrix = new Components.MatrixComponentType(SlotDefinition.matrixGrid, config.defaultStops, SlotDefinition.reelsets.get(config.defaultReelset));

        // Add transition (if configured)
        if (config.transition) {
            let transition: AbstractReelTransition;

            const tConfig = config.transition.options;

            if (config.transition.type === "spin") {
                transition = new Components.SpinReelTransitionType(tConfig.startGap, tConfig.stopGap, tConfig.quickStopGap, tConfig.allowPartialSkips);
            } else if (config.transition.type === "cascade") {
                transition = new Components.DropReelTransitionType(tConfig);
            } else if (config.transition.type === "animate") {
                transition = new Components.AnimateReelTransitionType(tConfig.startGap, tConfig.stopGap, tConfig.quickStopGap);
            }

            if (transition) {
                this.matrix.setTransition(transition);
            }

        }

        this.addDefaultSymbolBehaviours(this.matrix);
    }

    private static getSymbolBehaviourType(behaviourId: string): typeof AbstractSymbolBehaviour {
        if (behaviourId === "debug") {
            return DebugSymbolBehaviour;
        } else if (behaviourId === "animate") {
            return AnimateSymbolBehaviour;
        } else if (behaviourId === "animateOver") {
            return AnimateOverSymbolBehaviour;
        } else if (behaviourId === "blink") {
            return BlinkSymbolBehaviour;
        } else if (behaviourId === "zoom") {
            return ZoomSymbolBehaviour;
        } else if (behaviourId === "wobble") {
            return WobbleSymbolBehaviour;
        } else if (behaviourId === "landPulse") {
            return LandPulseSymbolBehaviour;
        } else if (behaviourId === "highlightDim") {
            return TintDimSymbolBehaviour;
        } else if (behaviourId === "winDim") {
            return TintDimSymbolBehaviour;
        } else if (behaviourId === "cycleDim") {
            return TintDimSymbolBehaviour;
        } else if (behaviourId === "highlightImageDim") {
            return ImageDimSymbolBehaviour;
        } else if (behaviourId === "winImageDim") {
            return ImageDimSymbolBehaviour;
        } else if (behaviourId === "cycleImageDim") {
            return ImageDimSymbolBehaviour;
        } else if (behaviourId === "landAnimateOver") {
            return LandAnimateOverSymbolBehaviour;
        } else if (behaviourId === "landAnimate") {
            return LandAnimateSymbolBehaviour;
        } else if (behaviourId === "expandAnimate") {
            return ExpandAnimateSymbolBehaviour;
        } else if (behaviourId === "anticipateAnimate") {
            return AnticipateAnimateSymbolBehaviour;
        } else if (behaviourId === "anticipateAnimateOver") {
            return AnticipateAnimateOverSymbolBehaviour;
        } else if (behaviourId === "stick") {
            return StickSymbolBehaviour;
        } else if (behaviourId === "stopBounce") {
            return StopBounceSymbolBehaviour;
        } else if (behaviourId === "animateBehind") {
            return AnimateBehindSymbolBehaviour;
        } else if (behaviourId === "remove") {
            return RemoveAnimateSymbolBehaviour;
        } else {
            Logger.error(`Invalid behaviour specified: ${behaviourId}`);
        }
    }

    private static createBetBar() {
        this.betBar = new Components.BetBarComponentType();
        this.gamble = new Components.GambleComponentType();
    }

    private static createBigWin() {
        const config = Components.config.bigWin as BigWinConfig;
        const tiers = config.tiers;

        this.bigWin = new Components.BigWinComponentType();
        this.bigWin.countupMode = config.linearCountup ? CountMode.LINEAR : CountMode.TIERED;
        this.bigWin.upgrade = config.upgrade;

        if (tiers) {
            for (const tier of tiers) {
                this.bigWin.addWinTier(tier);
            }
        }
    }

    private static createLines() {
        this.lines = new Components.LinesComponentType(SlotDefinition.lines.values);
    }

    private static createCelebrateWin() {
        this.celebrateWin = new Components.CelebrateWinComponentType();
    }

    private static createPrompts() {
        this.prompt = new Components.PromptComponentType();

        if (Components.config.summary) {
            if (Components.config.summary.defaultTime) {
                PromptComponent.DEFAULT_TIME = Components.config.summary.defaultTime;
            }
        }
    }

    private static createAlert() {
        this.alert = new Components.AlertComponentType();
    }

    private static createHeader() {
        this.header = new Components.HeaderComponentType();
    }

    private static createFooter() {
        this.footer = new Components.FooterComponentType();
    }

    private static createBanner() {
        this.banner = new Components.BannerComponentType();

        for (const mode of Object.keys(Components.config.banner)) {
            this.banner.addScenesForMode(mode, Components.config.banner[mode]);
        }

        this.banner.setMode("default");
        this.banner.start();
    }
}
